import { isToday } from "@/utils/date-utils";
import { GroupedResult, DateGroupingOptions, KeyGroupingOptions } from "./types";

/** Label groupByDate assigns to today's bucket. */
export const TODAY_GROUP_LABEL = "Today";
/** Label groupByDate assigns to yesterday's bucket. */
export const YESTERDAY_GROUP_LABEL = "Yesterday";

/**
 * `hideFirstGroupHeader` predicate: hide the first group's header only when it
 * is today. A past-date first group (e.g. over a weekend, when the newest story
 * is Friday's) keeps its header.
 */
export const hideFirstHeaderIfToday = (firstGroupTitle: string | undefined) =>
  firstGroupTitle === TODAY_GROUP_LABEL;

/**
 * Check if a date is yesterday
 */
export const isYesterday = (date: Date | string): boolean => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const checkDate = typeof date === "string" ? new Date(date) : date;

  return (
    checkDate.getFullYear() === yesterday.getFullYear() &&
    checkDate.getMonth() === yesterday.getMonth() &&
    checkDate.getDate() === yesterday.getDate()
  );
};

/**
 * Group items by date with support for "Today" and "Yesterday" special cases
 *
 * @example
 * ```typescript
 * const grouped = GroupingUtils.groupByDate(
 *   headlines,
 *   (item) => item.published_at,
 *   { includeToday: true, includeYesterday: true }
 * );
 * ```
 *
 * @param items - Array of items to group
 * @param dateExtractor - Function to extract date from each item
 * @param options - Grouping options
 * @returns Grouped result with groups, counts, and flattened items
 */
export function groupByDate<T>(
  items: T[],
  dateExtractor: (item: T) => Date | string,
  options: DateGroupingOptions = {}
): GroupedResult<T> {
  const {
    includeToday = true,
    includeYesterday = false,
    dateFormat = { weekday: "long", month: "short", day: "numeric" },
    sortOrder = "desc",
  } = options;

  // Group items by date key
  const grouped = items.reduce(
    (acc, item) => {
      const date = dateExtractor(item);

      // Validate date before processing
      if (date === undefined || date === null) {
        console.error("groupByDate: Invalid date extracted from item:", item);
        // Skip this item or add to an "Unknown Date" group
        const fallbackKey = "Unknown Date";
        if (!acc[fallbackKey]) {
          acc[fallbackKey] = [];
        }
        acc[fallbackKey].push(item);
        return acc;
      }

      const dateObj = typeof date === "string" ? new Date(date) : date;

      // Validate the resulting Date object
      if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
        console.error("groupByDate: Invalid Date object created from:", date);
        // Add to fallback group
        const fallbackKey = "Unknown Date";
        if (!acc[fallbackKey]) {
          acc[fallbackKey] = [];
        }
        acc[fallbackKey].push(item);
        return acc;
      }

      let dateKey: string;

      // Check for special cases
      if (includeToday && isToday(dateObj)) {
        dateKey = TODAY_GROUP_LABEL;
      } else if (includeYesterday && isYesterday(dateObj)) {
        dateKey = YESTERDAY_GROUP_LABEL;
      } else {
        // Format date using provided options
        dateKey = dateObj.toLocaleDateString("en-US", dateFormat);
      }

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(item);

      return acc;
    },
    {} as Record<string, T[]>
  );

  // Sort groups
  const groups = Object.keys(grouped).sort((a, b) => {
    // "Today" always comes first
    if (a === "Today") return -1;
    if (b === "Today") return 1;

    // "Yesterday" comes second
    if (a === "Yesterday") return -1;
    if (b === "Yesterday") return 1;

    // "Unknown Date" always comes last
    if (a === "Unknown Date") return 1;
    if (b === "Unknown Date") return -1;

    // Sort other dates by timestamp
    const dateA = new Date(a).getTime();
    const dateB = new Date(b).getTime();

    return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
  });

  // Build result with validation
  const groupCounts = groups.map((group) => {
    const count = grouped[group]?.length ?? 0;
    // Validate count is a valid positive number
    return typeof count === "number" && count >= 0 ? count : 0;
  });

  const flatItems = groups.flatMap((group) => grouped[group] ?? []);

  // Build item to group map
  const itemToGroupMap: number[] = [];
  groups.forEach((_, groupIndex) => {
    const count = groupCounts[groupIndex];
    for (let i = 0; i < count; i++) {
      itemToGroupMap.push(groupIndex);
    }
  });

  // Validate the results before returning
  if (flatItems.length !== itemToGroupMap.length) {
    console.error(
      "groupByDate: Mismatch between flatItems and itemToGroupMap lengths:",
      flatItems.length,
      itemToGroupMap.length
    );
  }

  return {
    groups,
    groupCounts,
    items: flatItems,
    itemToGroupMap,
  };
}

/**
 * Group items by a custom key extractor function
 *
 * @example
 * ```typescript
 * const grouped = GroupingUtils.groupByKey(
 *   stories,
 *   (item) => item.category,
 *   { sortGroups: (a, b) => a.localeCompare(b) }
 * );
 * ```
 *
 * @param items - Array of items to group
 * @param keyExtractor - Function to extract group key from each item
 * @param options - Grouping options
 * @returns Grouped result with groups, counts, and flattened items
 */
export function groupByKey<T>(
  items: T[],
  keyExtractor: (item: T) => string,
  options: KeyGroupingOptions = {}
): GroupedResult<T> {
  const { sortGroups } = options;

  // Group items by key
  const grouped = items.reduce(
    (acc, item) => {
      const key = keyExtractor(item);

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);

      return acc;
    },
    {} as Record<string, T[]>
  );

  // Get groups and optionally sort them
  let groups = Object.keys(grouped);
  if (sortGroups) {
    groups = groups.sort(sortGroups);
  }

  // Build result with validation
  const groupCounts = groups.map((group) => {
    const count = grouped[group]?.length ?? 0;
    // Validate count is a valid positive number
    return typeof count === "number" && count >= 0 ? count : 0;
  });

  const flatItems = groups.flatMap((group) => grouped[group] ?? []);

  // Build item to group map
  const itemToGroupMap: number[] = [];
  groups.forEach((_, groupIndex) => {
    const count = groupCounts[groupIndex];
    for (let i = 0; i < count; i++) {
      itemToGroupMap.push(groupIndex);
    }
  });

  // Validate the results before returning
  if (flatItems.length !== itemToGroupMap.length) {
    console.error(
      "groupByKey: Mismatch between flatItems and itemToGroupMap lengths:",
      flatItems.length,
      itemToGroupMap.length
    );
  }

  return {
    groups,
    groupCounts,
    items: flatItems,
    itemToGroupMap,
  };
}

/**
 * No grouping - returns items as a single flat list
 * Useful for maintaining consistent API when grouping is optional
 *
 * @example
 * ```typescript
 * const grouped = GroupingUtils.noGrouping(items);
 * // Can still use grouped.items just like grouped data
 * ```
 *
 * @param items - Array of items
 * @returns Grouped result with a single group containing all items
 */
export function noGrouping<T>(items: T[]): GroupedResult<T> {
  return {
    groups: ["All Items"],
    groupCounts: [items.length],
    items: items,
    itemToGroupMap: items.map(() => 0),
  };
}

/**
 * Utility object with all grouping functions
 * Use this for cleaner imports: `import { GroupingUtils } from ...`
 */
export const GroupingUtils = {
  groupByDate,
  groupByKey,
  noGrouping,
  hideFirstHeaderIfToday,
};
